import type { ItemFieldRow } from './item-field-reader';
import { readInventoryTemplateId } from './item-field-reader';
import { createInventoryReadContext, isThinnedInventoryRead } from './inventory-read-context';
import { createLayerValueReader, MissingLayerValueInventory } from './layer-value-reader';
import type { LayerValueDependencies } from './layer-value-reader';
import { phpJsonString } from './php-json-values';

export interface InventoryThinScalarDependencies extends LayerValueDependencies {
    /** query_full_item_arr(id, suppress_missing_item_warning: true).
     * Known absence may return null; unavailable query coverage must throw. */
    queryTemplate: (id: string) => unknown;
    /** Supply only inside a compile memo scope. Share across its readers, discard
     * or clear at scope exit, and never use it as a full-template blob cache. */
    templateValueMemo?: Map<string, Map<string, unknown>>;
}
const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}

/** Native ft_inventory_thin_scalar_read over supplied definitions. Local reads
 * retain computed/provider precedence while suppressing only this instance's
 * bridge. The guard is restored before any template fallback. Fat instances
 * follow the ordinary reader and cannot resurrect newly authored template data. */
export function createInventoryThinScalarReader(deps: InventoryThinScalarDependencies): {
    read: (row: ItemFieldRow, name: string) => unknown;
} {
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => {
        if (!deps.resolveInventoryInstance) throw new MissingLayerValueInventory(id);
        return deps.resolveInventoryInstance(id);
    });
    const scalar = createLayerValueReader({...deps, inventoryContext});
    return {read: (row, name) => {
        if (!arrayLike(row) || name === null || arrayLike(name)) throw new TypeError('Invalid native thin scalar reader arguments');
        name = phpJsonString(name);
        if (!isThinnedInventoryRead(row)) return scalar.read(row, name);
        const local = inventoryContext.withoutInstanceBridge(row, () => scalar.read(row, name));
        if (local !== null) return local;
        const id = readInventoryTemplateId(row);
        if (id === null) return null;
        const memo = deps.templateValueMemo?.get(id);
        if (memo?.has(name)) return clone(memo.get(name));
        const template = deps.queryTemplate(id);
        const value = arrayLike(template) ? scalar.read(template, name) : null;
        if (deps.templateValueMemo) {
            // Query/provider callbacks can populate another layer for this same
            // template. Preserve that nested write, as PHP's keyed assignment does.
            const values = deps.templateValueMemo.get(id) ?? new Map<string, unknown>();
            values.set(name, clone(value));
            deps.templateValueMemo.set(id, values);
        }
        return value;
    }};
}
